import React, {Component} from "react";
import {Header} from "../general/Header";
import ship from "../../../assets/ship.png";
import stueckgut from "../../../assets/stueckgut.jpg";
import environment from "../../../assets/environment.png";
import spinner from "../../../assets/spinner.gif";
import {ImTruck} from "react-icons/all";
import {IconContext} from "react-icons";
import {FirebaseService} from "../../../services/firebase/FirebaseService";
import DeliveryProgressBar from "../../components/DeliveryProgressBar";
import {DeliveryCategory} from "../../components/DeliveryCategory";
import {DeliveryInfoContainer} from "../../components/DeliveryInfoContainer";
import {DeliveryFreeKerbside} from "../../components/DeliveryFreeKerbside";
import {DeliveryFirstMetaInfo} from "../../components/DeliveryFirstMetaInfo";
import {Footer} from "../general/Footer";
import {NotTherePopUp} from "../../components/NotTherePopUp";
import {NeedHelpPopUp} from "../../components/NeedHelpPopUp";
import {Link} from "react-router-dom";
import {RequestPayload} from "../types/RequestPayload";

const queryString = require("query-string");

/**
 * Local interface to define the types of the state
 */
interface IState {
  searchIsActive: string;
  isLoading: boolean;
  trackingID?: string;
  emptyTrackingID: boolean;
  searchResult?: RequestPayload;

  isNotTherePopUpVisible: boolean;
  isNeedHelpPopUpVisible: boolean;
}

/**
 * Cross Innovation Class 2021
 *
 * Team: Kühne + Nagel
 *
 * Kunden Web App
 *
 * This class the is the main page of the WebApp. Here the user can search for his delivery and learn
 * about bulky goods
 *
 * @author Tobias Ramm <winf103614@fh-wedel,de>
 * @version last modification: 29.06.2021
 *
 */
export default class LandingSite extends Component<any> {
  state: Readonly<IState> = {
    searchIsActive: "0",
    isLoading: true,
    emptyTrackingID: false,

    isNotTherePopUpVisible: false,
    isNeedHelpPopUpVisible: false,
  };

  /**
   * Will be called when the users enters a trackingID
   */
  async handleSearch() {
    const { trackingID } = this.state;

    if (!trackingID) {
      this.setState({
        emptyTrackingID: true,
      });
    } else {
      this.setState({
        searchIsActive: "1",
        isLoading: true,
      });

      // event listener that has to be in this class and not outsourced, because for every child change the
      // state will be set new and the view rerender. This is needed to ensure the realTime updates.
      await FirebaseService.dbRef()
        .deliveries()
        .child(trackingID)
        .on("value", (snapshot) => {
          this.setState({
            searchResult: snapshot.val(),
            isLoading: false,
          });

          snapshot.val();
        });

      return this.state.searchResult;
    }
  }

  /**
   * componentDidMount() is invoked immediately after a component is mounted (inserted into the tree).
   * Here it will be checked if there are url parameters for the trackingID and the a possible
   * popUp that should be opened
   */
  async componentDidMount(): Promise<void> {
    const parsed = queryString.parse(window.location.search);

    if (!!parsed.trackID) {
      // when the trackingID is set as an url parameter
      this.setState(
        {
          trackingID: parsed.trackID,
        },
        async () => {
          await this.handleSearch();

          // timeout to ensure the data has been loaded
          setTimeout(() => {
            // when the needHelp is set as an url parameter open the needHelpPopUp
            if (
              !!parsed.needHelp &&
              this.state.searchResult &&
              !this.state.searchResult.noHelpNeeded &&
              !this.state.searchResult.bookedHelp
            ) {
              this.setState({
                isNeedHelpPopUpVisible: true,
              });

              // when the notPresent is set as an url parameter open the notPresentPopUp
            } else if (
              !!parsed.notThere &&
              this.state.searchResult &&
              !this.state.searchResult.isPresent &&
              !this.state.searchResult.chosenNotPresentOption
            ) {
              this.setState({
                isNotTherePopUpVisible: true,
              });
            }
          }, 1500);
        }
      );
    }
  }

  render() {
    return (
      <div>
        <Header />
        <div className={"landingContainer"}>
          <div className={"ship"} style={{ background: `url(${ship})` }} />

          <div
            className={"trackingContainer"}
            data-search-is-active={this.state.searchIsActive}
          >
            <h2 data-search-is-active={this.state.searchIsActive}>
              Sie erhalten ein Stückgut?
            </h2>

            <div className={"trackingInputContainer"}>
              <div className={"trackingInput"}>
                <IconContext.Provider
                  value={{ color: "#003369", className: "truckIcon" }}
                >
                  <ImTruck />
                </IconContext.Provider>
                <input
                  type={"text"}
                  placeholder={"Tracking- Nummer eingeben"}
                  value={this.state.trackingID}
                  onChange={(evt) =>
                    this.setState({ trackingID: evt.target.value })
                  }
                />
              </div>
              <button onClick={() => this.handleSearch()}>Suchen</button>
            </div>

            <div
              className={"searchResultContainer"}
              data-search-is-active={this.state.searchIsActive}
            >
              {this.state.isLoading && (
                <img src={spinner} width={80} height={80} alt={"spinner"} />
              )}

              {!this.state.isLoading && !!this.state.searchResult && (
                <div className={"searchResultFoundContainer"}>
                  <h1>Ihre Speditionslieferung:</h1>

                  <DeliveryProgressBar searchResult={this.state.searchResult} />

                  <DeliveryFirstMetaInfo
                    status={this.state.searchResult.status}
                    estimatedTimeOfArrival={
                      this.state.searchResult.estimatedTimeOfArrival
                    }
                    onPressNotPresent={() =>
                      this.setState({ isNotTherePopUpVisible: true })
                    }
                    searchResult={this.state.searchResult}
                    trackingID={this.state.trackingID as string}
                  />

                  <DeliveryInfoContainer
                    searchResult={this.state.searchResult}
                  />

                  <DeliveryCategory category={Number(this.state.searchResult.category)} />

                  <DeliveryFreeKerbside
                    onPressNeedHelp={() =>
                      this.setState({ isNeedHelpPopUpVisible: true })
                    }
                    searchResult={this.state.searchResult}
                    trackingID={this.state.trackingID as string}
                  />
                </div>
              )}

              {!this.state.isLoading && !this.state.searchResult && (
                <h1>Kein Stückgut zu der Tracking ID gefunden</h1>
              )}
            </div>
          </div>

          <div className={"landing_info_box"}>
            <div
              className={"landing_info_image"}
              style={{ background: `url(${stueckgut})` }}
            />

            <div className={"landing_info_text_container"}>
              <h2>Was ist ein Stückgut?</h2>
              <h4>
                Als Stückgut werden solche Sendungen bezeichnet, die sich am
                Stück transportieren lassen. Sie haben meist eine
                überdurchschnittliche Größe und ein hohes Gewicht und sollten
                dementsprechend achtsam transportiert werden.   Stückgüter
                können in Form von einer Palette, einem Packstück oder einer
                Kiste geliefert werden und sind somit meist nicht händisch
                transportierbar.
              </h4>
            </div>
          </div>

          <div className={"landing_info_box"}>
            <div
              className={"landing_info_image"}
              style={{ background: `url(${environment})` }}
            />

            <div className={"landing_info_text_container"}>
              <h2>Was bewirkt eine Annahmeverweigerung?</h2>
              <h4>
                {" "}
                Annahmevereweigerungen sorgen nicht nur für mehr Kosten auf der
                Seite des Lieferanten und der Herrsteller, sondern belastet auch
                die Umwelt.
                <br />
                <br />
                <Link to="/unsuccessfulDelivery">
                  <h4>
                    Lernen Sie hier was eine Annahmeverweigerung genau bewirkt
                  </h4>
                </Link>
              </h4>
            </div>
          </div>
        </div>

        {!this.state.isLoading && !!this.state.searchResult && (
          <div>
            <NotTherePopUp
              isVisible={this.state.isNotTherePopUpVisible}
              onHide={() => this.setState({ isNotTherePopUpVisible: false })}
              searchResult={this.state.searchResult}
              trackingID={this.state.trackingID as string}
            />

            <NeedHelpPopUp
              isVisible={this.state.isNeedHelpPopUpVisible}
              onHide={() => this.setState({ isNeedHelpPopUpVisible: false })}
              searchResult={this.state.searchResult}
              trackingID={this.state.trackingID as string}
            />
          </div>
        )}
        <Footer />
      </div>
    );
  }
}
